import { useState, useSyncExternalStore } from 'react'

import { Spacer, Text } from '@chakra-ui/react'
import { useConnectionState, useZero } from '@rocicorp/zero/react'
import { useAuth } from '@saas-ui/auth-provider'
import { Avatar, type AvatarProps, Menu, toast } from '@saas-ui/react'
import { onlineManager, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate } from '@tanstack/react-router'

import { CheckIcon } from '#components/icons/check-icon.tsx'

import { useWorkspace } from '../hooks/use-workspace'
import { useWorkspaces } from '../hooks/use-workspaces'
import {
  getWorkspaceConnectionStatus,
  isFailedConnectionQuery,
} from './workspace-connection-status'
import { WorkspaceMenu } from './workspace-menu'

const subscribeOnline = (listener: () => void) =>
  onlineManager.subscribe(listener)
const getOnline = () => onlineManager.isOnline()

const WorkspaceLogo: React.FC<AvatarProps> = (props) => {
  const { src, ...rest } = props
  return (
    <Avatar
      display="inline-flex"
      src={src}
      size="xs"
      borderRadius="full"
      {...rest}
    />
  )
}

export const WorkspacesMenu: React.FC = () => {
  const navigate = useNavigate({ from: '/$workspace/' })

  const { logOut } = useAuth()

  const queryClient = useQueryClient()
  const zero = useZero()
  const sync = useConnectionState()
  const online = useSyncExternalStore(subscribeOnline, getOnline, getOnline)
  const queryCache = queryClient.getQueryCache()
  const requestFailed = useSyncExternalStore(
    (listener) => queryCache.subscribe(listener),
    () => queryCache.getAll().some(isFailedConnectionQuery),
    () => false,
  )
  const connection = getWorkspaceConnectionStatus(online, sync, requestFailed)
  const [retrying, setRetrying] = useState(false)

  const retryConnection = async () => {
    if (retrying) return
    setRetrying(true)
    try {
      if (connection?.action === 'sync') await zero.connection.connect()
      else if (connection?.action === 'request') {
        await queryClient.refetchQueries(
          { predicate: isFailedConnectionQuery },
          { throwOnError: true },
        )
      }
    } catch {
      toast.error({ title: 'Unable to reconnect. Please try again.' })
    } finally {
      setRetrying(false)
    }
  }

  const workspace = useWorkspace()
  const workspaces = useWorkspaces()

  const activeWorkspace = (function () {
    for (const i in workspaces) {
      if (workspaces[i]?.slug === workspace) {
        return workspaces[i]
      }
    }
    return workspaces[0]
  })()

  const setWorkspace = (workspace: string) => {
    navigate({ to: `/${workspace}` })
  }

  const logOutAndClearCache = () => {
    logOut().then(() => {
      queryClient.clear()
      navigate({ to: '/login' })
    })
  }

  const activeLogo = (
    <WorkspaceLogo name={activeWorkspace?.label} src={activeWorkspace?.logo} />
  )

  return (
    <WorkspaceMenu
      label={activeWorkspace?.label}
      logo={activeLogo}
      connection={connection}
      retrying={retrying}
      onRetry={() => void retryConnection()}
      onSignIn={() => void navigate({ to: '/login' })}
      onReload={() => window.location.reload()}
    >
      <Menu.ItemGroup title="Workspaces">
        {workspaces.map(({ slug, label, logo, ...props }) => {
          return (
            <Menu.Item
              key={slug}
              value={slug}
              onClick={() => setWorkspace(slug)}
              {...props}
            >
              <WorkspaceLogo name={label} src={logo} />

              <Text>{label}</Text>
              <Spacer />
              {slug === activeWorkspace?.slug ? <CheckIcon /> : null}
            </Menu.Item>
          )
        })}
      </Menu.ItemGroup>
      <Menu.Separator />
      <Menu.Item value="workspace-settings" asChild>
        <Link to="/$workspace/settings/workspace" params={{ workspace }}>
          Settings
        </Link>
      </Menu.Item>
      <Menu.Item value="create-workspace" asChild>
        <Link to="/getting-started">Create a workspace</Link>
      </Menu.Item>
      <Menu.Separator />
      <Menu.Item
        value="logout"
        onClick={() => {
          logOutAndClearCache()
        }}
      >
        Logout
      </Menu.Item>
    </WorkspaceMenu>
  )
}
